/**
 * The rules presets page's body: every preset's settings side by side (one
 * column per preset, one row per setting, then one per roles action), and
 * under the grid, per preset, the rooms, place defaults, templates and
 * repeats that use it (collapsed by default). Read-only. Pure presentation
 * (no data fetching, no router), so the proof script renders it from fixtures.
 */
import * as React from "react";
import { ChevronDown, LayoutGrid, Link2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { SampleChip } from "@/features/places/place-today";
import {
  ARCHIVED_LINE,
  COMING_EVENTS_LINE,
  NOBODY,
  ROLES_LEGEND,
  ROOMS_NOT_LISTED_LINE,
  asLabel,
  columnOf,
  presetGridOf,
  presetsInOrder,
  usedNowhere,
  usesSummary,
  whereUsedNotes,
  type GridCell,
  type PresetColumn,
  type PresetGrid,
  type PresetUses,
  type Uses,
  type WhereUsed,
  type PresetsPageData,
} from "@/features/presets/presets";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/** The page's one sentence under the title. */
export const PRESETS_INTRO =
  "Every rules preset's settings side by side, and where each one is used. Read-only.";

export function PresetsHeading({ sample }: { sample: boolean }) {
  return (
    <div>
      <div className="flex items-center gap-2.5">
        <h1 className="text-2xl font-extrabold tracking-[-.02em]">Rules presets</h1>
        {sample && <SampleChip />}
      </div>
      <p className="mt-1 text-[13.5px] text-muted-foreground">{PRESETS_INTRO}</p>
    </div>
  );
}

/** The page body from data. `open`: every where-used entry starts open (the
 *  proof); on the page they start collapsed. */
export function PresetsView({ data, sample, open = false }: { data: PresetsPageData; sample: boolean; open?: boolean }) {
  const grid = presetGridOf(data.presets);
  return (
    <div className="flex flex-col gap-5">
      <PresetsHeading sample={sample} />
      {grid.columns.length === 0 ? (
        <Card className="p-5 text-[13px] text-muted-foreground">No rules presets yet.</Card>
      ) : (
        <>
          <Grid grid={grid} />
          <WhereUsedSection data={data} open={open} />
        </>
      )}
    </div>
  );
}

function ColumnHead({ column }: { column: PresetColumn }) {
  return (
    <div className="flex items-start gap-2">
      {column.iconUrl && <img src={column.iconUrl} alt="" className="mt-0.5 size-5 shrink-0 rounded-md object-cover" />}
      <div className="min-w-0">
        <div className="text-[13px] font-bold text-foreground">{column.name}</div>
        <div className={cn("font-mono text-[11px]", column.slug ? "text-text-3" : "italic text-text-3")}>
          {column.slug ?? "no slug"}
        </div>
      </div>
    </div>
  );
}

function Cell({ cell }: { cell: GridCell }) {
  if (cell.kind === "text") return <span>{cell.text}</span>;
  if (cell.roles.length === 0) return <span className="text-text-3">{NOBODY}</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {cell.roles.map((role, i) => (
        <code
          key={i}
          className="rounded-md border border-border bg-panel-2 px-1.5 py-px font-mono text-[11px] text-foreground"
        >
          {role}
        </code>
      ))}
    </span>
  );
}

const STICKY = "sticky left-0 z-10 bg-card";
/** Every preset column's width: the table sizes to its content (`w-max`), so
 *  a fixed-width box in each cell is what makes long text wrap. */
const COLUMN = "w-[200px] break-words";

/** The grid: sticky first column; it scrolls sideways inside its card, never the page. */
function Grid({ grid }: { grid: PresetGrid }) {
  const firstRoles = grid.rows.findIndex((r) => r.kind === "roles");
  return (
    <Card className="overflow-hidden" data-section="Grid">
      <div className="overflow-x-auto">
        <table className="w-max min-w-full border-separate border-spacing-0 text-left text-[12.5px]">
          <thead>
            <tr>
              <th
                scope="col"
                className={cn(STICKY, "border-b border-r border-border px-4 py-3 text-[11px] font-bold uppercase tracking-[.05em] text-text-3")}
              >
                Setting
              </th>
              {grid.columns.map((c) => (
                <th key={c.id} scope="col" data-preset={c.id} className="border-b border-border px-4 py-3 align-top font-normal">
                  <div className={COLUMN}>
                    <ColumnHead column={c} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((row, i) => (
              <React.Fragment key={row.key}>
                {i === firstRoles && (
                  <tr>
                    <th
                      scope="rowgroup"
                      className={cn(STICKY, "border-b border-r border-border bg-panel-2 px-4 pb-2 pt-4 text-[11px] font-bold uppercase tracking-[.05em] text-text-3")}
                    >
                      Who may…
                    </th>
                    <td colSpan={grid.columns.length} className="border-b border-border bg-panel-2" />
                  </tr>
                )}
                <tr data-row={row.key}>
                  <th
                    scope="row"
                    className={cn(STICKY, "whitespace-nowrap border-b border-r border-border px-4 py-2.5 align-top font-semibold")}
                  >
                    {row.label}
                    {row.deprecated && (
                      <span className="ml-2 rounded-md border border-dashed border-border-strong px-1.5 py-px text-[10px] font-semibold text-text-3">
                        deprecated
                      </span>
                    )}
                  </th>
                  {row.cells.map((cell, j) => (
                    <td key={grid.columns[j]!.id} className="border-b border-border px-4 py-2.5 align-top">
                      <div className={COLUMN}>
                        <Cell cell={cell} />
                      </div>
                    </td>
                  ))}
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-4 py-3 text-xs text-text-3">{ROLES_LEGEND}</p>
    </Card>
  );
}

/** A where-used group's list, or why it has none. */
function UseGroup<T>({
  title,
  uses,
  none,
  children,
}: {
  title: string;
  uses: Uses<T>;
  none: string;
  children: (use: T) => React.ReactNode;
}) {
  return (
    <section data-group={title} className="py-2.5 first:pt-0 last:pb-0">
      <h4 className="mb-1.5 text-[12.5px] font-semibold">{title}</h4>
      {uses === "failed" ? (
        <p className="text-[12.5px] text-text-3">Did not read.</p>
      ) : uses.length === 0 ? (
        <p className="text-[12.5px] text-text-3">{none}</p>
      ) : (
        <ul className="flex flex-col gap-1 text-[13px]">{uses.map((u, i) => <li key={i}>{children(u)}</li>)}</ul>
      )}
    </section>
  );
}

function PresetUsesList({ column, uses }: { column: PresetColumn; uses: PresetUses }) {
  return (
    <div className="flex flex-col divide-y divide-border">
      <UseGroup title="Rooms" uses={uses.rooms} none="None.">
        {(r) => (
          <>
            {r.place} <span className="text-text-3">→ {r.room}</span>
            {r.title && ` ${r.title}`}
          </>
        )}
      </UseGroup>
      <UseGroup title="Place defaults for new tables" uses={uses.defaults} none="None.">
        {(d) => d.place}
      </UseGroup>
      <UseGroup
        title="Templates"
        uses={uses.templates}
        none={column.slug ? "None." : "None: this preset has no slug, and templates name presets by slug."}
      >
        {(t) => (
          <>
            {t.name} <span className="text-text-3">· {asLabel(t.as)}</span>
          </>
        )}
      </UseGroup>
      <UseGroup title="Repeats" uses={uses.repeats} none="None.">
        {(r) => (
          <>
            {r.name} <span className="text-text-3">· through {r.template} · {asLabel(r.as)}</span>
          </>
        )}
      </UseGroup>
      <p className="pt-2.5 text-[12.5px] text-text-3">{COMING_EVENTS_LINE}</p>
    </div>
  );
}

/** One preset's where-used entry: its summary line; opened, its lists. A
 *  preset used nowhere has nothing to open. */
function PresetUsesEntry({ column, uses, open: initiallyOpen }: { column: PresetColumn; uses: PresetUses; open: boolean }) {
  const [open, setOpen] = React.useState(initiallyOpen);
  const summary = usesSummary(uses);
  const nowhere = usedNowhere(uses);
  return (
    <li data-uses={column.id} className="py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold">{column.name}</div>
          <div className={cn("text-[12px]", nowhere ? "text-text-3" : "text-muted-foreground")}>{summary}</div>
          {nowhere && <div className="text-[12px] text-text-3">{COMING_EVENTS_LINE}</div>}
        </div>
        {!nowhere && (
          <Button variant="subtle" size="sm" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? "Hide" : "Show where"}
            <ChevronDown className={cn("transition-transform", open && "rotate-180")} />
          </Button>
        )}
      </div>
      {open && !nowhere && (
        <div className="mt-2.5 rounded-[10px] border border-border bg-panel-2 px-3.5 py-3">
          <PresetUsesList column={column} uses={uses} />
        </div>
      )}
    </li>
  );
}

function WhereUsedSection({ data, open }: { data: PresetsPageData; open: boolean }) {
  const w: WhereUsed = data.whereUsed;
  const notes = whereUsedNotes(w);
  return (
    <Card>
      <CardHeader data-section="Where used">
        <CardTitle>
          <Link2 /> Where used
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 pt-3">
        <ul className="flex flex-col divide-y divide-border">
          {presetsInOrder(data.presets).map((p) => {
            const uses = w.byPreset.get(p.id);
            return uses ? <PresetUsesEntry key={p.id} column={columnOf(p)} uses={uses} open={open} /> : null;
          })}
        </ul>
        {w.unknownSlugs.length > 0 && (
          <div data-group="Unknown slugs" className="rounded-[10px] border border-dashed border-border-strong px-3.5 py-3 text-[12.5px]">
            <div className="mb-1 font-semibold">Named by templates, matching no preset listed here</div>
            <ul className="flex flex-col gap-0.5">
              {w.unknownSlugs.map((u) => (
                <li key={u.slug}>
                  <code className="font-mono text-[11.5px]">{u.slug}</code>{" "}
                  <span className="text-muted-foreground">
                    ← {[...u.templates.map((t) => `template ${t}`), ...u.repeats.map((r) => `repeat ${r}`)].join(", ")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex flex-col gap-1 text-xs text-text-3">
          <p className="flex items-center gap-1.5">
            <LayoutGrid className="size-3.5" /> {ROOMS_NOT_LISTED_LINE}
          </p>
          <p>{ARCHIVED_LINE}</p>
          {notes.map((n) => (
            <p key={n}>{n}</p>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
